import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout, { adminHeaders, useAdminAuthed } from "@/components/AdminLayout";
import AdminCRUD from "@/components/AdminCRUD";
import { Star, MapPin, ExternalLink, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rupees = (paise) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format((paise || 0) / 100);

// ============================= FAQs =============================
export const AdminFaqsPage = () => (
  <AdminLayout title="FAQs" subtitle="Questions surfaced on the storefront, per product or globally">
    <AdminCRUD
      endpoint="/admin/faqs"
      labelSingular="FAQ"
      testIdPrefix="faq"
      searchKeys={["question", "answer", "category"]}
      defaults={{ question: "", answer: "", category: "General", display_order: 0, status: "published", is_global: true }}
      columns={[
        { name: "question", label: "Question", render: (v) => <span className="font-medium text-[var(--hk-ink)]">{v}</span> },
        { name: "category", label: "Category" },
        { name: "linked_product", label: "Product" },
        { name: "display_order", label: "Order" },
        { name: "status", label: "Status", render: (v) => <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">{v}</span> },
      ]}
      fields={[
        { name: "question", label: "Question", kind: "text", required: true },
        { name: "answer", label: "Answer", kind: "textarea", rows: 5, required: true },
        { name: "category", label: "Category", kind: "text", help: "e.g. Product, Usage, Shipping, Payment, Returns" },
        { name: "linked_product", label: "Linked product slug (optional)", kind: "text", help: "Leave empty for global FAQ" },
        { name: "is_global", label: "Show on main FAQ page", kind: "boolean" },
        { name: "display_order", label: "Display order", kind: "number" },
        { name: "status", label: "Status", kind: "select", options: ["published", "draft", "hidden"] },
      ]}
    />
  </AdminLayout>
);

// ============================= Botanicals =============================
export const AdminBotanicalsPage = () => (
  <AdminLayout title="Botanicals" subtitle="The Botanicals grid on the homepage — fully editable">
    <AdminCRUD
      endpoint="/admin/botanicals"
      labelSingular="Botanical"
      testIdPrefix="bot"
      searchKeys={["name_en", "name_hi", "scientific"]}
      defaults={{ name_en: "", name_hi: "", name_sa: "", scientific: "", plant_part: "", short: "", story: "", image: "", show_on_home: true, display_order: 0, status: "published" }}
      columns={[
        { name: "name_en", label: "Name" },
        { name: "scientific", label: "Scientific" },
        { name: "plant_part", label: "Part" },
        { name: "show_on_home", label: "Home", render: (v) => v ? "Yes" : "No" },
        { name: "display_order", label: "Order" },
        { name: "status", label: "Status" },
      ]}
      fields={[
        { name: "name_en", label: "English name", kind: "text", required: true },
        { name: "name_hi", label: "Hindi name", kind: "text" },
        { name: "name_sa", label: "Sanskrit name", kind: "text" },
        { name: "scientific", label: "Scientific name", kind: "text" },
        { name: "plant_part", label: "Plant part (Berry, Root, Resin…)", kind: "text" },
        { name: "short", label: "One-line tagline", kind: "text" },
        { name: "story", label: "Detailed story", kind: "textarea", rows: 5 },
        { name: "image", label: "Image URL", kind: "image" },
        { name: "show_on_home", label: "Show on homepage grid", kind: "boolean" },
        { name: "display_order", label: "Display order", kind: "number" },
        { name: "status", label: "Status", kind: "select", options: ["published", "draft", "hidden"] },
      ]}
    />
  </AdminLayout>
);

// ============================= Ingredients =============================
export const AdminIngredientsPage = () => (
  <AdminLayout title="Ingredients" subtitle="Reusable ingredient library shown on product pages">
    <AdminCRUD
      endpoint="/admin/ingredients"
      labelSingular="Ingredient"
      testIdPrefix="ing"
      searchKeys={["name", "description"]}
      defaults={{ name: "", description: "", traditional_use: "", safety_note: "", image: "", linked_products: [], display_order: 0, status: "published" }}
      columns={[
        { name: "name", label: "Name" },
        { name: "linked_products", label: "Linked products", render: (v) => (v || []).join(", ") },
        { name: "display_order", label: "Order" },
        { name: "status", label: "Status" },
      ]}
      fields={[
        { name: "name", label: "Ingredient name", kind: "text", required: true },
        { name: "description", label: "Description", kind: "textarea" },
        { name: "traditional_use", label: "Traditional use", kind: "textarea" },
        { name: "safety_note", label: "Safety / caution note", kind: "text" },
        { name: "image", label: "Image URL", kind: "image" },
        { name: "linked_products", label: "Linked product slugs", kind: "tags", help: "comma separated" },
        { name: "display_order", label: "Display order", kind: "number" },
        { name: "status", label: "Status", kind: "select", options: ["published", "draft", "hidden"] },
      ]}
    />
  </AdminLayout>
);

// ============================= Categories & Collections =============================
export const AdminCategoriesPage = ({ kind = "category" }) => (
  <AdminLayout title={kind === "collection" ? "Collections" : "Categories"} subtitle={`Manage storefront ${kind === "collection" ? "collections" : "categories"}`}>
    <AdminCRUD
      endpoint="/admin/categories"
      labelSingular={kind === "collection" ? "Collection" : "Category"}
      testIdPrefix={kind}
      searchKeys={["name", "slug"]}
      defaults={{ name: "", slug: "", description: "", image: "", display_order: 0, kind, linked_products: [], status: "published" }}
      columns={[
        { name: "name", label: "Name" },
        { name: "slug", label: "Slug" },
        { name: "kind", label: "Kind" },
        { name: "linked_products", label: "Products", render: (v) => (v || []).length },
        { name: "display_order", label: "Order" },
        { name: "status", label: "Status" },
      ]}
      fields={[
        { name: "name", label: "Name", kind: "text", required: true },
        { name: "slug", label: "Slug (URL-safe)", kind: "text", required: true },
        { name: "description", label: "Description", kind: "textarea" },
        { name: "image", label: "Image URL", kind: "image" },
        { name: "kind", label: "Kind", kind: "select", options: ["category", "collection"] },
        { name: "linked_products", label: "Linked product slugs", kind: "tags" },
        { name: "seo_title", label: "SEO title", kind: "text" },
        { name: "seo_description", label: "SEO description", kind: "textarea" },
        { name: "display_order", label: "Display order", kind: "number" },
        { name: "status", label: "Status", kind: "select", options: ["published", "draft", "hidden"] },
      ]}
    />
  </AdminLayout>
);

// ============================= Reviews =============================
const StarRow = ({ n }) => (
  <span className="inline-flex text-amber-500">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={12} fill={i < n ? "currentColor" : "none"} />
    ))}
  </span>
);

export const AdminReviewsPage = () => (
  <AdminLayout title="Reviews" subtitle="Approve or feature customer reviews">
    <AdminCRUD
      endpoint="/admin/reviews"
      labelSingular="Review"
      testIdPrefix="rev"
      searchKeys={["customer_name", "title", "body", "product_slug"]}
      defaults={{ customer_name: "", rating: 5, title: "", body: "", product_slug: "", image: "", verified: false, featured: false, status: "pending" }}
      columns={[
        { name: "customer_name", label: "Customer" },
        { name: "rating", label: "Rating", render: (v) => <StarRow n={Number(v || 0)} /> },
        { name: "product_slug", label: "Product" },
        { name: "verified", label: "Verified", render: (v) => v ? "✓" : "" },
        { name: "featured", label: "Featured", render: (v) => v ? "★" : "" },
        { name: "status", label: "Status" },
      ]}
      fields={[
        { name: "customer_name", label: "Customer name", kind: "text", required: true },
        { name: "rating", label: "Rating (1-5)", kind: "number" },
        { name: "title", label: "Title", kind: "text" },
        { name: "body", label: "Review body", kind: "textarea", rows: 4 },
        { name: "product_slug", label: "Product slug", kind: "text" },
        { name: "image", label: "Image URL", kind: "image" },
        { name: "verified", label: "Verified purchase", kind: "boolean" },
        { name: "featured", label: "Featured", kind: "boolean" },
        { name: "status", label: "Status", kind: "select", options: ["pending", "approved", "rejected", "hidden"] },
      ]}
    />
  </AdminLayout>
);

// ============================= Coupons =============================
export const AdminCouponsPage = () => (
  <AdminLayout title="Coupons" subtitle="Discount codes, automatic sales, BXGY promos">
    <AdminCRUD
      endpoint="/admin/coupons"
      labelSingular="Coupon"
      testIdPrefix="cpn"
      searchKeys={["code", "description"]}
      defaults={{ code: "", kind: "percent", value: 10, min_order: 0, max_discount: 0, product_slugs: [], category_slugs: [], per_customer_limit: 0, usage_limit: 0, buy_x: 0, get_y: 0, automatic: false, enabled: true, description: "" }}
      columns={[
        { name: "code", label: "Code", render: (v) => <span className="font-mono">{v}</span> },
        { name: "kind", label: "Kind" },
        { name: "value", label: "Value", render: (v, r) => r.kind === "percent" ? `${v}%` : rupees(v) },
        { name: "min_order", label: "Min order", render: (v) => rupees(v) },
        { name: "used_count", label: "Used" },
        { name: "enabled", label: "Enabled", render: (v) => v ? "✓" : "—" },
      ]}
      fields={[
        { name: "code", label: "Coupon code", kind: "text", required: true },
        { name: "description", label: "Internal description", kind: "text" },
        { name: "kind", label: "Discount kind", kind: "select", options: [{ value: "percent", label: "% Discount" }, { value: "flat", label: "Flat ₹ off (paise)" }, { value: "bxgy", label: "Buy X Get Y" }] },
        { name: "value", label: "Value (% or paise)", kind: "number" },
        { name: "min_order", label: "Minimum order (paise)", kind: "number" },
        { name: "max_discount", label: "Max discount cap (paise, 0 = no cap)", kind: "number" },
        { name: "buy_x", label: "Buy X (BXGY only)", kind: "number" },
        { name: "get_y", label: "Get Y (BXGY only)", kind: "number" },
        { name: "product_slugs", label: "Restrict to product slugs", kind: "tags" },
        { name: "category_slugs", label: "Restrict to categories", kind: "tags" },
        { name: "per_customer_limit", label: "Per-customer limit (0 = unlimited)", kind: "number" },
        { name: "usage_limit", label: "Total usage limit (0 = unlimited)", kind: "number" },
        { name: "starts_at", label: "Starts at (ISO date)", kind: "text" },
        { name: "ends_at", label: "Ends at (ISO date)", kind: "text" },
        { name: "automatic", label: "Auto-apply", kind: "boolean" },
        { name: "enabled", label: "Enabled", kind: "boolean" },
      ]}
    />
  </AdminLayout>
);

// ============================= Influencers =============================
export const AdminInfluencersPage = () => (
  <AdminLayout title="Influencers / Affiliates" subtitle="Attribute sales to creators">
    <AdminCRUD
      endpoint="/admin/influencers"
      labelSingular="Influencer"
      testIdPrefix="infl"
      searchKeys={["name", "handle", "code"]}
      defaults={{ name: "", email: "", phone: "", handle: "", channel: "instagram", code: "", commission_pct: 10, notes: "", status: "active" }}
      columns={[
        { name: "name", label: "Name" },
        { name: "handle", label: "Handle" },
        { name: "channel", label: "Channel" },
        { name: "code", label: "Code" },
        { name: "commission_pct", label: "Commission", render: (v) => `${v}%` },
        { name: "status", label: "Status" },
      ]}
      fields={[
        { name: "name", label: "Name", kind: "text", required: true },
        { name: "email", label: "Email", kind: "text" },
        { name: "phone", label: "Phone", kind: "text" },
        { name: "handle", label: "Social handle", kind: "text" },
        { name: "channel", label: "Channel", kind: "select", options: ["instagram", "youtube", "twitter", "linkedin", "blog", "other"] },
        { name: "code", label: "Coupon / attribution code", kind: "text" },
        { name: "commission_pct", label: "Commission %", kind: "number" },
        { name: "notes", label: "Notes", kind: "textarea" },
        { name: "status", label: "Status", kind: "select", options: ["active", "paused", "archived"] },
      ]}
    />
  </AdminLayout>
);

// ============================= Blog =============================
export const AdminBlogPage = () => (
  <AdminLayout title="Blog" subtitle="Long-form posts for SEO and storytelling">
    <AdminCRUD
      endpoint="/admin/blog"
      labelSingular="Post"
      testIdPrefix="blog"
      searchKeys={["title", "slug", "tags"]}
      defaults={{ title: "", slug: "", excerpt: "", body: "", cover: "", author: "HerbKranti", tags: [], seo_title: "", seo_description: "", status: "draft", published_at: null }}
      columns={[
        { name: "title", label: "Title" },
        { name: "slug", label: "Slug" },
        { name: "author", label: "Author" },
        { name: "status", label: "Status" },
      ]}
      fields={[
        { name: "title", label: "Title", kind: "text", required: true },
        { name: "slug", label: "Slug", kind: "text", required: true },
        { name: "excerpt", label: "Excerpt", kind: "textarea", rows: 2 },
        { name: "body", label: "Body (HTML or Markdown)", kind: "textarea", rows: 10 },
        { name: "cover", label: "Cover image URL", kind: "image" },
        { name: "author", label: "Author", kind: "text" },
        { name: "tags", label: "Tags", kind: "tags" },
        { name: "seo_title", label: "SEO title", kind: "text" },
        { name: "seo_description", label: "SEO description", kind: "textarea" },
        { name: "status", label: "Status", kind: "select", options: ["draft", "published", "hidden"] },
      ]}
    />
  </AdminLayout>
);

// ============================= Media Library =============================
export const AdminMediaPage = () => (
  <AdminLayout title="Media Library" subtitle="Reusable image URLs used across products, banners and content">
    <AdminCRUD
      endpoint="/admin/media"
      labelSingular="Asset"
      testIdPrefix="media"
      searchKeys={["name", "url", "alt"]}
      defaults={{ url: "", name: "", alt: "", caption: "", kind: "image", size_kb: 0 }}
      columns={[
        { name: "url", label: "Preview", render: (v) => <img src={v} alt="" className="h-10 rounded" /> },
        { name: "name", label: "Name" },
        { name: "alt", label: "Alt text" },
        { name: "kind", label: "Type" },
      ]}
      fields={[
        { name: "url", label: "Image URL", kind: "image", required: true },
        { name: "name", label: "Name", kind: "text" },
        { name: "alt", label: "Alt text (SEO)", kind: "text" },
        { name: "caption", label: "Caption", kind: "text" },
        { name: "kind", label: "Kind", kind: "select", options: ["image", "video", "document"] },
      ]}
    />
  </AdminLayout>
);

// ============================= Orders (list) =============================
export const AdminOrdersPage = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [openOrder, setOpenOrder] = useState(null);
  const pw = useAdminAuthed();
  const load = async () => {
    if (!pw) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/orders`, { headers: adminHeaders() });
      setRows(data || []);
    } catch { toast.error("Failed to load orders"); }
    finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pw]);

  const [ratesData, setRatesData] = useState(null);   // {results, recommended}
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rateSort, setRateSort] = useState("price");

  const compareRates = async (o) => {
    setRatesLoading(true);
    setRatesData(null);
    try {
      const r = await axios.post(`${API}/admin/orders/${o.id}/rates/compare`, {}, { headers: adminHeaders() });
      setRatesData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rate comparison failed");
    } finally {
      setRatesLoading(false);
    }
  };

  const selectRate = async (o, provider, rate) => {
    const sh = await createShipment(o, { provider, courier_id: rate.courier_id, rate: rate.rate, courier_name: rate.courier_name });
    if (sh) {
      setOpenOrder((c) => ({ ...c, shipment: sh, shiprocket_order_id: sh.provider_order_id || c.shiprocket_order_id }));
      setRatesData(null);
    }
  };

  const cancelShipment = async (o) => {
    try {
      await axios.post(`${API}/admin/orders/${o.id}/shipment/cancel`, {}, { headers: adminHeaders() });
      toast.success("Shipment cancelled — you can compare and select again.");
      refetchOpenOrder(o.id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  // Refetch an order into the open modal (used after failures so the shipment_error banner renders)
  const refetchOpenOrder = async (orderId) => {
    try {
      const r = await axios.get(`${API}/admin/orders/${orderId}`, { headers: adminHeaders() });
      setOpenOrder((cur) => (cur && cur.id === orderId ? { ...cur, ...r.data } : cur));
    } catch { /* best-effort */ }
  };
  const createShipment = async (o, selection = null) => {
    try {
      const r = await axios.post(`${API}/admin/orders/${o.id}/shipment/create`, selection || {}, { headers: adminHeaders() });
      const sh = r.data?.shipment || r.data;
      toast.success(sh?.awb ? `Shipment created — AWB ${sh.awb}` : `Shipment created in ${sh?.provider_name || sh?.provider || "provider"} (ID ${sh?.provider_order_id || sh?.shipment_id})`);
      load();
      return sh;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create shipment failed");
      refetchOpenOrder(o.id);
      return null;
    }
  };
  // Fulfilment actions inside the order detail modal — updates the open order in place
  const shipAction = async (o, action, label, { silent = false } = {}) => {
    try {
      const r = await axios.post(`${API}/admin/orders/${o.id}/shipment/${action}`, {}, { headers: adminHeaders() });
      const sh = r.data?.shipment;
      if (sh) setOpenOrder((cur) => (cur && cur.id === o.id ? { ...cur, shipment: sh, shiprocket_order_id: sh.provider_order_id || cur.shiprocket_order_id, fulfilment_status: sh.status === "delivered" ? "delivered" : cur.fulfilment_status } : cur));
      if (action === "serviceability") {
        const couriers = r.data?.couriers || [];
        toast.success(couriers.length ? `${couriers.length} courier(s) serviceable — cheapest: ${couriers[0]?.courier_name} ₹${couriers[0]?.rate}` : "Checked");
      } else if (action === "refresh" && r.data?.awb_synced !== undefined) {
        if (r.data.awb_synced) toast.success(r.data.message || "AWB synced from provider");
        else if (!silent) toast.info(r.data.message || "AWB not yet generated at the provider");
      } else if (!silent) {
        toast.success(`${label} done`);
      }
      load();
      return r.data;
    } catch (e) {
      if (silent) return null;
      toast.error(e?.response?.data?.detail || `${label} failed`);
      setOpenOrder((cur) => (cur ? { ...cur, shipment_error: e?.response?.data?.detail || cur.shipment_error } : cur));
      refetchOpenOrder(o.id);
      return null;
    }
  };
  // Download label: open the saved label, or generate it on demand (needs an AWB) and then open it.
  const downloadLabel = async (o) => {
    if (o.shipment?.label_url) { window.open(o.shipment.label_url, "_blank", "noopener"); return; }
    const res = await shipAction(o, "label", "Label");
    const url = res?.shipment?.label_url;
    if (url) window.open(url, "_blank", "noopener");
  };
  // When an order opens with a provider shipment but no AWB yet, silently sync from the provider once
  // (covers AWBs generated directly in the Shiprocket panel) so the status + buttons reflect reality.
  const awbSyncedRef = useRef(new Set());
  useEffect(() => {
    const sh = openOrder?.shipment;
    if (!openOrder || !sh || sh.awb || sh.status === "cancelled" || !sh.provider || sh.provider === "in-house") return;
    if (awbSyncedRef.current.has(openOrder.id)) return;
    awbSyncedRef.current.add(openOrder.id);
    shipAction(openOrder, "refresh", "AWB sync", { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOrder?.id]);
  const setStatus = async (o, status) => {
    try { await axios.post(`${API}/admin/orders/${o.id}/shipment/status`, { status }, { headers: adminHeaders() });
      toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Status update failed"); }
  };
  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [r.customer_name, r.email, r.phone, r.id].some((x) => String(x || "").toLowerCase().includes(s));
  });
  return (
    <AdminLayout title="Orders" subtitle={`${rows.length} total orders — search by customer, phone, email or order id`}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full max-w-md h-10 px-3 rounded-md border border-[var(--hk-border)] bg-white/70 mb-4" data-testid="orders-search" />
      <div className="hk-card overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-[var(--hk-warm-ivory)] text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Order</th>
              <th className="text-left px-4 py-3 font-medium">Customer</th>
              <th className="text-left px-4 py-3 font-medium">Items</th>
              <th className="text-left px-4 py-3 font-medium">Total</th>
              <th className="text-left px-4 py-3 font-medium">Payment</th>
              <th className="text-left px-4 py-3 font-medium">Shipment</th>
              <th className="text-left px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--hk-ink-2)]">Loading…</td></tr>}
            {!busy && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--hk-ink-2)]">No orders.</td></tr>}
            {!busy && filtered.map((o) => {
              const sh = o.shipment;
              return (
              <tr key={o.id} className="border-t border-[var(--hk-border)] cursor-pointer hover:bg-[var(--hk-warm-ivory)]/50" data-testid={`order-row-${o.id}`} onClick={() => setOpenOrder(o)}>
                <td className="px-4 py-3 font-mono text-[11px]">{o.id.slice(0, 8)}<br /><span className="text-[10px] text-[var(--hk-ink-3)]">{new Date(o.created_at).toLocaleDateString("en-IN")}</span></td>
                <td className="px-4 py-3">
                  <p className="font-medium">{o.customer_name}</p>
                  <p className="text-[11px] text-[var(--hk-ink-2)]">{o.email}</p>
                  <p className="text-[11px] text-[var(--hk-ink-2)]">{o.phone}</p>
                </td>
                <td className="px-4 py-3 max-w-[220px]">{(o.items || []).map((i) => `${i.name} × ${i.qty}`).join(", ")}</td>
                <td className="px-4 py-3"><b>{rupees(o.total)}</b><br /><span className="text-[11px] text-[var(--hk-ink-3)]">{o.payment_method}</span></td>
                <td className="px-4 py-3">
                  <span className={
                    "text-[11px] px-2 py-0.5 rounded-full border capitalize " +
                    (o.payment_status === "paid" ? "border-green-200 bg-green-50 text-green-800"
                      : o.payment_status === "failed" ? "border-red-200 bg-red-50 text-red-700"
                      : o.payment_status === "cancelled" ? "border-stone-300 bg-stone-100 text-stone-700"
                      : o.payment_status === "whatsapp_handoff" ? "border-blue-200 bg-blue-50 text-blue-700"
                      : "border-amber-200 bg-amber-50 text-amber-800")
                  } data-testid={`order-payment-status-${o.id}`}>{(o.payment_status || "pending").replace(/_/g, " ")}</span>
                </td>
                <td className="px-4 py-3">
                  {sh ? (
                    <div className="space-y-0.5">
                      <p className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">{sh.courier}</p>
                      <p className="font-mono text-[11px]">{sh.awb}</p>
                      <p className="text-[11px] text-[var(--hk-ink-3)]">{(sh.status || "").replace(/_/g, " ")}</p>
                    </div>
                  ) : <span className="text-[11px] text-[var(--hk-ink-3)]">—</span>}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {!sh && o.payment_status === "paid" && (
                    <button onClick={() => createShipment(o)} className="text-[11px] px-2 py-1 rounded border border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]" data-testid={`ship-create-${o.id}`}>Create shipment</button>
                  )}
                  {sh && (sh.awb || sh.provider === "in-house") && (
                    <button onClick={() => downloadLabel(o)} className="text-[11px] px-2 py-1 rounded border border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]" data-testid={`ship-label-${o.id}`}>Download label</button>
                  )}
                  {sh && (
                    <select onChange={(e) => e.target.value && setStatus(o, e.target.value)} defaultValue=""
                      className="text-[11px] px-2 py-1 rounded border border-[var(--hk-border)] bg-white mt-1" data-testid={`ship-status-${o.id}`}>
                      <option value="">Update status…</option>
                      <option value="picked_up">Picked up</option>
                      <option value="in_transit">In transit</option>
                      <option value="out_for_delivery">Out for delivery</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Order detail modal — incl. Delivery Location with one-click Google Maps */}
      {openOrder && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpenOrder(null)} data-testid="order-detail-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="hk-h5">Order {openOrder.id.slice(0, 8)}</h3>
                <p className="text-[12px] text-[var(--hk-ink-3)] mt-0.5">{new Date(openOrder.created_at).toLocaleString("en-IN")} · {(openOrder.payment_status || "pending").replace(/_/g, " ")}</p>
              </div>
              <button onClick={() => setOpenOrder(null)} className="text-[var(--hk-ink-3)] hover:text-[var(--hk-ink)]" data-testid="order-detail-close"><X size={18} /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 text-[13px]">
              <div className="p-4 rounded-lg bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)]">
                <p className="text-[10px] uppercase tracking-widest text-[var(--hk-ink-2)] mb-2">Customer</p>
                <p className="font-medium">{openOrder.customer_name}</p>
                <p className="text-[var(--hk-ink-2)]">{openOrder.email}</p>
                <p className="text-[var(--hk-ink-2)]">{openOrder.phone}</p>
              </div>
              <div className="p-4 rounded-lg bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)]" data-testid="order-detail-address">
                <p className="text-[10px] uppercase tracking-widest text-[var(--hk-ink-2)] mb-2">Delivery Address</p>
                <p>{openOrder.address_line1}</p>
                {openOrder.address_line2 && <p className="text-[var(--hk-ink-2)]">Landmark: {openOrder.address_line2}</p>}
                <p className="text-[var(--hk-ink-2)]">{openOrder.city}, {openOrder.state} — {openOrder.pincode}</p>
                {openOrder.notes && <p className="text-[var(--hk-ink-3)] mt-1 italic">"{openOrder.notes}"</p>}
              </div>
            </div>

            {/* DELIVERY LOCATION — private, admin-only */}
            {(openOrder.latitude != null || openOrder.google_maps_url) && (
              <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/50 space-y-3" data-testid="order-detail-location">
                <p className="text-[10px] uppercase tracking-widest text-emerald-800 flex items-center gap-1.5"><MapPin size={12} /> Delivery Location</p>
                {openOrder.latitude != null && (
                  <p className="font-mono text-[13px]" data-testid="order-detail-latlng">{Number(openOrder.latitude).toFixed(6)}, {Number(openOrder.longitude).toFixed(6)}</p>
                )}
                <p className="text-[12px] text-[var(--hk-ink-2)] capitalize">Source: {(openOrder.location_source || "map").replace(/_/g, " ")}</p>
                <a
                  href={openOrder.google_maps_url || `https://www.google.com/maps?q=${openOrder.latitude},${openOrder.longitude}`}
                  target="_blank" rel="noreferrer"
                  className="hk-btn-primary inline-flex items-center gap-2 h-10 px-4"
                  data-testid="order-open-maps-btn"
                >
                  <ExternalLink size={14} /> Open in Google Maps
                </a>
              </div>
            )}

            <div className="text-[13px]">
              <p className="text-[10px] uppercase tracking-widest text-[var(--hk-ink-2)] mb-2">Items</p>
              {(openOrder.items || []).map((i, idx) => (
                <p key={idx} className="py-1 border-b border-[var(--hk-border)] last:border-0">{i.name} × {i.qty} — <b>{rupees((i.unit_price || 0) * (i.qty || 1))}</b></p>
              ))}
              <p className="pt-2 text-right font-semibold">Total {rupees(openOrder.total)}</p>
            </div>

            {/* Fulfilment — provider IDs + actions */}
            <div className="p-4 rounded-lg border border-[var(--hk-border)] space-y-3" data-testid="order-detail-fulfilment">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[10px] uppercase tracking-widest text-[var(--hk-ink-2)]">Fulfilment</p>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-[var(--hk-border)]" data-testid="order-fulfilment-status">
                  {openOrder.shipment
                    ? (openOrder.shipment.pickup_status ? "Pickup scheduled" : openOrder.shipment.awb ? "AWB generated" : "Created in " + (openOrder.shipment.provider_name || openOrder.shipment.provider))
                    : "Not created"}
                  {openOrder.shipment?.status === "delivered" && " · Delivered"}
                </span>
              </div>
              <p className="text-[12px]">Payment: <b className={openOrder.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}>{(openOrder.payment_status || "pending").toUpperCase()}</b></p>
              {openOrder.shipment && (
                <div className="text-[12px] space-y-1 font-mono" data-testid="order-provider-ids">
                  {openOrder.shipment.provider_order_id && <p>{openOrder.shipment.provider_name || "Provider"} Order ID: {openOrder.shipment.provider_order_id}</p>}
                  {openOrder.shipment.shipment_id && <p>Shipment ID: {openOrder.shipment.shipment_id}</p>}
                  {openOrder.shipment.awb && <p>AWB: {openOrder.shipment.awb}</p>}
                  <p>Courier: {openOrder.shipment.courier || "—"}{openOrder.shipment.courier_company_id ? ` (ID ${openOrder.shipment.courier_company_id})` : ""}</p>
                  {openOrder.shipment.payment_mode && <p>Payment mode: {openOrder.shipment.payment_mode}</p>}
                  {openOrder.shipment.provider_status_raw && <p>Provider status (raw): {openOrder.shipment.provider_status_raw}</p>}
                  {openOrder.shipment.label_url && <p>Label: <a href={openOrder.shipment.label_url} target="_blank" rel="noreferrer" className="underline text-[var(--hk-orange-deep)]" data-testid="order-label-link">Download label</a></p>}
                  {openOrder.shipment.shipping_cost != null && <p>Shipping cost: ₹{openOrder.shipment.shipping_cost}{openOrder.shipment.auto_selected ? " (auto-selected)" : ""}</p>}
                  {openOrder.shipment.last_sync_at && <p className="text-[10px] text-[var(--hk-ink-3)]">Last sync: {new Date(openOrder.shipment.last_sync_at).toLocaleString("en-IN")}</p>}
                </div>
              )}
              {(openOrder.shipment_error || openOrder.shipment?.error) && (
                <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="order-shipment-error">
                  {openOrder.shipment_error || openOrder.shipment?.error}
                </p>
              )}
              {/* Multi-courier rate comparison — read-only until "Use This Rate" */}
              {openOrder.payment_status === "paid" && (!openOrder.shipment || openOrder.shipment.status === "cancelled") && (
                <div className="space-y-2" data-testid="rate-compare-section">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => compareRates(openOrder)} disabled={ratesLoading}
                      className="text-[11.5px] px-3 py-1.5 rounded-md bg-[var(--hk-ink)] text-white disabled:opacity-50" data-testid="fulfil-compare">
                      {ratesLoading ? "Comparing live rates…" : "Compare Shipping Rates"}
                    </button>
                    {ratesData && (
                      <select value={rateSort} onChange={(e) => setRateSort(e.target.value)}
                        className="text-[11.5px] px-2 py-1.5 rounded-md border border-[var(--hk-border)] bg-white" data-testid="rate-sort">
                        <option value="price">Lowest price</option>
                        <option value="eta">Fastest delivery</option>
                        <option value="provider">Provider</option>
                      </select>
                    )}
                  </div>
                  {ratesData && (
                    <div className="border border-[var(--hk-border)] rounded-md overflow-hidden" data-testid="rate-results">
                      {(() => {
                        const rows = [];
                        for (const res of ratesData.results || []) {
                          if (!res.ok) {
                            rows.push({ provider: res.provider, provider_name: res.provider_name, error: res.error });
                          } else if (!(res.rates || []).length) {
                            rows.push({ provider: res.provider, provider_name: res.provider_name, error: "No courier service available for this pincode" });
                          } else {
                            for (const rate of res.rates) rows.push({ provider: res.provider, provider_name: res.provider_name, ...rate });
                          }
                        }
                        const rec = ratesData.recommended;
                        if (rateSort === "price") rows.sort((a, b) => (a.rate ?? 9e9) - (b.rate ?? 9e9));
                        else if (rateSort === "eta") rows.sort((a, b) => (parseInt(a.etd) || 99) - (parseInt(b.etd) || 99));
                        else rows.sort((a, b) => String(a.provider_name).localeCompare(String(b.provider_name)));
                        if (!rows.length) return <p className="text-[12px] text-[var(--hk-ink-2)] p-3">No enabled providers returned rates.</p>;
                        return rows.map((r, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[var(--hk-border)] last:border-0 text-[12px]" data-testid={`rate-row-${r.provider}-${i}`}>
                            <div className="min-w-0">
                              <p className="font-medium truncate">
                                {r.provider_name}{r.courier_name ? ` · ${r.courier_name}` : ""}
                                {rec && r.rate != null && r.provider === rec.provider && r.courier_name === rec.courier_name && (
                                  <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase tracking-wider">Recommended</span>
                                )}
                              </p>
                              {r.error
                                ? <p className="text-red-600 text-[11px]">{r.error}</p>
                                : <p className="text-[var(--hk-ink-3)] text-[11px]">{r.etd ? `~${r.etd}` : "ETA on assignment"} · Prepaid{r.cod_charge != null && r.cod_charge !== 0 ? ` · COD +₹${r.cod_charge}` : ""}{r.rto_charge != null && r.rto_charge !== 0 ? ` · RTO ₹${r.rto_charge}` : ""}{r.service_type ? ` · ${r.service_type}` : ""}</p>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {r.rate != null && <span className="font-semibold">₹{r.rate}</span>}
                              {!r.error && (
                                <button onClick={() => selectRate(openOrder, r.provider, r)}
                                  className="text-[11px] px-2.5 py-1 rounded-md bg-[var(--hk-orange-deep)] text-white" data-testid={`rate-use-${r.provider}-${i}`}>
                                  Use This Rate
                                </button>
                              )}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {openOrder.shipment && !["picked_up", "in_transit", "out_for_delivery", "delivered"].includes(openOrder.shipment.status) && (
                  <button onClick={() => cancelShipment(openOrder)}
                    className="text-[11.5px] px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50" data-testid="fulfil-cancel">Cancel Shipment / Change Selection</button>
                )}
                {!openOrder.shipment && openOrder.payment_status === "paid" && (
                  <button onClick={async () => { const sh = await createShipment(openOrder); if (sh) setOpenOrder((c) => ({ ...c, shipment: sh })); }}
                    className="text-[11.5px] px-3 py-1.5 rounded-md bg-[var(--hk-orange-deep)] text-white" data-testid="fulfil-create">Create Shipment</button>
                )}
                {openOrder.payment_status === "paid" && (
                  <button onClick={() => shipAction(openOrder, "serviceability", "Serviceability check")}
                    className="text-[11.5px] px-3 py-1.5 rounded-md border border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]" data-testid="fulfil-serviceability">Check Serviceability</button>
                )}
                {openOrder.shipment && openOrder.shipment.provider !== "in-house" && !openOrder.shipment.awb && openOrder.shipment.status !== "cancelled" && (openOrder.shipment.capabilities?.awb_generation !== false) && (
                  <button onClick={() => shipAction(openOrder, "awb", "AWB generation")}
                    className="text-[11.5px] px-3 py-1.5 rounded-md border border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]" data-testid="fulfil-awb">Generate AWB</button>
                )}
                {openOrder.shipment && openOrder.shipment.provider !== "in-house" && !openOrder.shipment.awb && openOrder.shipment.status !== "cancelled" && (
                  <button onClick={() => shipAction(openOrder, "refresh", "AWB sync")}
                    className="text-[11.5px] px-3 py-1.5 rounded-md border border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]" data-testid="fulfil-sync-awb">
                    Sync AWB from {openOrder.shipment.provider_name || openOrder.shipment.provider}
                  </button>
                )}
                {openOrder.shipment?.awb && (openOrder.shipment.capabilities?.label !== false) && (
                  <button onClick={() => downloadLabel(openOrder)}
                    className="text-[11.5px] px-3 py-1.5 rounded-md bg-[var(--hk-ink)] text-white hover:opacity-90" data-testid="fulfil-label">Download Label</button>
                )}
                {openOrder.shipment?.awb && !openOrder.shipment?.pickup_status && (openOrder.shipment.capabilities?.pickup_registration !== false) && (
                  <button onClick={() => shipAction(openOrder, "pickup", "Pickup scheduling")}
                    className="text-[11.5px] px-3 py-1.5 rounded-md border border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]" data-testid="fulfil-pickup">Schedule Pickup</button>
                )}
                {openOrder.shipment?.awb && (openOrder.shipment.capabilities?.tracking_history !== false) && (
                  <button onClick={() => shipAction(openOrder, "refresh", "Status sync")}
                    className="text-[11.5px] px-3 py-1.5 rounded-md border border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]" data-testid="fulfil-refresh">Refresh Status</button>
                )}
                {openOrder.shipment && openOrder.shipment.provider !== "in-house" && openOrder.shipment.capabilities?.reverse_shipment && ["delivered", "delivery_exception", "rto"].includes(openOrder.shipment.status) && (
                  <button onClick={() => shipAction(openOrder, "reverse", "Reverse shipment")}
                    className="text-[11.5px] px-3 py-1.5 rounded-md border border-[var(--hk-border)] hover:bg-[var(--hk-warm-ivory)]" data-testid="fulfil-reverse">Create Reverse Shipment</button>
                )}
              </div>
            </div>

            {openOrder.shipment && openOrder.shipment.provider === "in-house" && (
              <div className="p-4 rounded-lg bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] text-[13px]">
                <p className="text-[10px] uppercase tracking-widest text-[var(--hk-ink-2)] mb-2">Shipment</p>
                <p>{openOrder.shipment.courier} · <span className="font-mono">{openOrder.shipment.awb}</span> · {(openOrder.shipment.status || "").replace(/_/g, " ")}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// ============================= Customers =============================
export const AdminCustomersPage = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const pw = useAdminAuthed();
  useEffect(() => {
    if (!pw) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await axios.get(`${API}/admin/customers`, { headers: adminHeaders() });
        setRows(data || []);
      } catch { toast.error("Failed to load customers"); }
      finally { setBusy(false); }
    })();
  }, [pw]);
  return (
    <AdminLayout title="Customers" subtitle={`${rows.length} registered customers`}>
      <div className="hk-card overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-[var(--hk-warm-ivory)] text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Mobile</th>
              <th className="text-left px-4 py-3 font-medium">Orders</th>
              <th className="text-left px-4 py-3 font-medium">Total spend</th>
              <th className="text-left px-4 py-3 font-medium">Last order</th>
              <th className="text-left px-4 py-3 font-medium">Joined</th>
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--hk-ink-2)]">Loading…</td></tr>}
            {!busy && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--hk-ink-2)]">No customers yet.</td></tr>}
            {!busy && rows.map((c) => (
              <tr key={c.id} className="border-t border-[var(--hk-border)]">
                <td className="px-4 py-3">{c.name}</td>
                <td className="px-4 py-3">{c.email}</td>
                <td className="px-4 py-3">{c.mobile}</td>
                <td className="px-4 py-3">{c.total_orders || 0}</td>
                <td className="px-4 py-3">{rupees(c.total_spend || 0)}</td>
                <td className="px-4 py-3 text-[var(--hk-ink-2)]">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString("en-IN") : "—"}</td>
                <td className="px-4 py-3 text-[var(--hk-ink-2)]">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN") : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

// ============================= Audit Logs =============================
export const AdminAuditPage = () => {
  const [rows, setRows] = useState([]);
  const pw = useAdminAuthed();
  useEffect(() => {
    if (!pw) return;
    axios.get(`${API}/admin/audit-logs`, { headers: adminHeaders() })
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Failed to load audit"));
  }, [pw]);
  return (
    <AdminLayout title="System Logs" subtitle="Every admin change is recorded here">
      <div className="hk-card overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-[var(--hk-warm-ivory)] text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Module</th>
              <th className="text-left px-4 py-3">Target</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--hk-ink-2)]">No log entries yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--hk-border)]">
                <td className="px-4 py-3 text-[var(--hk-ink-2)]">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">{r.actor}</td>
                <td className="px-4 py-3 uppercase text-[11px] tracking-widest">{r.action}</td>
                <td className="px-4 py-3">{r.module}</td>
                <td className="px-4 py-3 font-mono text-[11px]">{r.target}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

// ============================= Roles (informational Super Admin only) =============================
export const AdminRolesPage = () => (
  <AdminLayout title="Roles & Permissions" subtitle="Access-control model — Super Admin managed">
    <div className="hk-card p-6 max-w-3xl space-y-4 text-[14px]">
      <p className="text-[var(--hk-ink-2)]">HerbKranti currently uses a single Super Admin credential (session-stored). The following roles are provisioned in the schema and will surface here once granular user provisioning is enabled:</p>
      <ul className="list-disc pl-5 space-y-1.5 text-[var(--hk-ink)]">
        <li><b>Super Admin</b> — full access including payment credentials</li>
        <li><b>Admin</b> — everything except gateway secrets</li>
        <li><b>Content Manager</b> — Pages, FAQs, Botanicals, Blog, SEO, Media</li>
        <li><b>Product Manager</b> — Products, Categories, Collections, Ingredients, Reviews</li>
        <li><b>Inventory Manager</b> — Inventory, stock adjustments, low-stock alerts</li>
        <li><b>Order Manager</b> — Orders, refunds, shipping labels</li>
        <li><b>Customer Support</b> — Enquiries, Customers, Orders (view + notes)</li>
        <li><b>Marketing Manager</b> — Coupons, Influencers, Banners, Analytics</li>
        <li><b>Finance Viewer</b> — Read-only Reports, Analytics, Tax, Invoices</li>
      </ul>
      <p className="text-[13px] text-[var(--hk-ink-2)]">
        Use <em>Global Settings → Support email</em> to keep receipts routed correctly meanwhile.
      </p>
    </div>
  </AdminLayout>
);

// ============================= Reports & Analytics (summary reuse) =============================
export const AdminReportsPage = () => {
  const [data, setData] = useState(null);
  const pw = useAdminAuthed();
  useEffect(() => {
    if (!pw) return;
    axios.get(`${API}/admin/summary`, { headers: adminHeaders() }).then((r) => setData(r.data)).catch(() => {});
  }, [pw]);
  return (
    <AdminLayout title="Reports" subtitle="Sales, customer & inventory snapshots">
      {!data && <p className="text-[var(--hk-ink-2)]">Loading…</p>}
      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="hk-card p-5">
            <h3 className="hk-h5">Sales</h3>
            <ul className="mt-3 space-y-2 text-[14px]">
              <li className="flex justify-between"><span>Total orders</span><b>{data.total_orders}</b></li>
              <li className="flex justify-between"><span>Paid orders</span><b>{data.paid_orders}</b></li>
              <li className="flex justify-between"><span>Total sales</span><b>{rupees(data.total_sales_paise)}</b></li>
              <li className="flex justify-between"><span>Average order value</span><b>{rupees(data.aov_paise)}</b></li>
            </ul>
          </div>
          <div className="hk-card p-5">
            <h3 className="hk-h5">Catalogue & Customers</h3>
            <ul className="mt-3 space-y-2 text-[14px]">
              <li className="flex justify-between"><span>Products</span><b>{data.products}</b></li>
              <li className="flex justify-between"><span>Customers</span><b>{data.customers}</b></li>
              <li className="flex justify-between"><span>Enquiries</span><b>{data.enquiries}</b></li>
              <li className="flex justify-between"><span>Low stock items</span><b className={data.low_stock ? "text-amber-700" : ""}>{data.low_stock}</b></li>
              <li className="flex justify-between"><span>Out of stock</span><b className={data.out_stock ? "text-red-700" : ""}>{data.out_stock}</b></li>
            </ul>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export const AdminAnalyticsPage = () => AdminReportsPage();

// ============================= Integrations =============================
export const AdminIntegrationsPage = () => {
  const pw = useAdminAuthed();
  const [cfg, setCfg] = useState(null);       // meta_pixel
  const [otpCfg, setOtpCfg] = useState(null); // otp
  const [tgCfg, setTgCfg] = useState(null);   // telegram
  const [gmCfg, setGmCfg] = useState(null);   // google_maps
  const [saving, setSaving] = useState(false);
  const [savingOtp, setSavingOtp] = useState(false);
  const [savingTg, setSavingTg] = useState(false);
  const [savingGm, setSavingGm] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tgLog, setTgLog] = useState([]);

  useEffect(() => {
    if (!pw) return;
    (async () => {
      try {
        const [pix, otp, tg, gm] = await Promise.all([
          axios.get(`${API}/admin/settings/meta_pixel`, { headers: adminHeaders() }),
          axios.get(`${API}/admin/settings/otp`, { headers: adminHeaders() }),
          axios.get(`${API}/admin/settings/telegram`, { headers: adminHeaders() }),
          axios.get(`${API}/admin/settings/google_maps`, { headers: adminHeaders() }),
        ]);
        setCfg({
          enabled: !!pix.data?.enabled,
          pixel_id: pix.data?.pixel_id || "",
          test_event_code: pix.data?.test_event_code || "",
          capi_enabled: !!pix.data?.capi_enabled,
          capi_access_token: pix.data?.capi_access_token || "",
          notes: pix.data?.notes || "",
        });
        setOtpCfg({
          enabled: otp.data?.enabled !== false,
          enabled_live: !!otp.data?.enabled_live,
          provider: otp.data?.provider || "mock",
          auth_key: otp.data?.auth_key || "",
          template_id: otp.data?.template_id || "",
          sender_id: otp.data?.sender_id || "HKRANT",
          var_name: otp.data?.var_name || "otp",
          resend_cooldown: otp.data?.resend_cooldown || 30,
        });
        const defEvents = {
          new_visitor: true, returning_visitor: false, pageview: false,
          product_viewed: true, add_to_cart: true, checkout_started: true,
          payment_failed: true, payment_cancelled: true, payment_successful: true,
          order_confirmed: true, high_value_order: true,
          order_shipped: true, order_delivered: true,
        };
        setTgCfg({
          enabled: !!tg.data?.enabled,
          bot_token: tg.data?.bot_token || "",
          chat_id: tg.data?.chat_id || "",
          high_value_threshold_rupees: tg.data?.high_value_threshold_rupees ?? 5000,
          events: { ...defEvents, ...(tg.data?.events || {}) },
          last_success_at: tg.data?.last_success_at || null,
          last_error_at: tg.data?.last_error_at || null,
          last_error: tg.data?.last_error || "",
        });
        setGmCfg({
          enabled: !!gm.data?.enabled,
          api_key: gm.data?.api_key || "",
          autocomplete: gm.data?.autocomplete !== false,
          current_location: gm.data?.current_location !== false,
          map_pin: gm.data?.map_pin !== false,
          paste_link: gm.data?.paste_link !== false,
        });
      } catch (e) {
        toast.error("Failed to load Integrations settings");
        setCfg({ enabled: false, pixel_id: "", test_event_code: "", capi_enabled: false, capi_access_token: "", notes: "" });
        setOtpCfg({ enabled: true, enabled_live: false, provider: "mock", auth_key: "", template_id: "", sender_id: "HKRANT", var_name: "otp", resend_cooldown: 30 });
        setTgCfg({ enabled: false, bot_token: "", chat_id: "", high_value_threshold_rupees: 5000, events: {}, last_success_at: null, last_error: "" });
        setGmCfg({ enabled: false, api_key: "", autocomplete: true, current_location: true, map_pin: true, paste_link: true });
      }
    })();
  }, [pw]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setOtp = (k, v) => setOtpCfg((c) => ({ ...c, [k]: v }));
  const setTg = (k, v) => setTgCfg((c) => ({ ...c, [k]: v }));
  const setTgEvent = (k, v) => setTgCfg((c) => ({ ...c, events: { ...(c.events || {}), [k]: v } }));
  const setGm = (k, v) => setGmCfg((c) => ({ ...c, [k]: v }));

  const saveGm = async () => {
    if (gmCfg?.enabled && !(gmCfg.api_key || "").trim()) {
      toast.error("Paste your Google Maps API key before enabling.");
      return;
    }
    setSavingGm(true);
    try {
      const payload = {
        enabled: !!gmCfg.enabled,
        api_key: (gmCfg.api_key || "").trim(),
        autocomplete: !!gmCfg.autocomplete,
        current_location: !!gmCfg.current_location,
        map_pin: !!gmCfg.map_pin,
        paste_link: !!gmCfg.paste_link,
      };
      const r = await axios.put(`${API}/admin/settings/google_maps`, payload, { headers: adminHeaders() });
      setGmCfg({ ...gmCfg, ...r.data });
      toast.success("Google Maps settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingGm(false);
    }
  };

  const saveTg = async () => {
    setSavingTg(true);
    try {
      const payload = {
        enabled: !!tgCfg.enabled,
        bot_token: (tgCfg.bot_token || "").trim(),
        chat_id: (tgCfg.chat_id || "").trim(),
        high_value_threshold_rupees: Number(tgCfg.high_value_threshold_rupees) || 0,
        events: tgCfg.events,
      };
      const r = await axios.put(`${API}/admin/settings/telegram`, payload, { headers: adminHeaders() });
      setTgCfg({ ...tgCfg, ...r.data, events: { ...(tgCfg.events || {}), ...(r.data?.events || {}) } });
      toast.success("Telegram settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingTg(false);
    }
  };

  const testTgConnection = async () => {
    try {
      const r = await axios.post(`${API}/admin/telegram/test-connection`, { bot_token: tgCfg.bot_token }, { headers: adminHeaders() });
      if (r.data?.ok) toast.success(`Connected: @${r.data.bot_username}`);
      else toast.error(r.data?.message || "Connection failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  const sendTgTestMsg = async () => {
    try {
      const r = await axios.post(`${API}/admin/telegram/test-message`, {}, { headers: adminHeaders() });
      if (r.data?.ok) toast.success("Test message sent — check Telegram");
      else toast.error(r.data?.error || "Send failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const loadTgLog = async () => {
    try {
      const r = await axios.get(`${API}/admin/telegram/log?limit=50`, { headers: adminHeaders() });
      setTgLog(r.data || []);
    } catch { toast.error("Failed to load log"); }
  };

  const save = async () => {
    if (cfg?.enabled && !/^\d{6,20}$/.test((cfg.pixel_id || "").trim())) {
      toast.error("Enter a valid numeric Pixel ID (6-20 digits) before enabling.");
      return;
    }
    if (cfg?.capi_enabled && !(cfg.capi_access_token || "").trim()) {
      toast.error("Paste the CAPI access token before enabling server-side Conversions API.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        enabled: !!cfg.enabled,
        pixel_id: (cfg.pixel_id || "").trim(),
        test_event_code: (cfg.test_event_code || "").trim(),
        capi_enabled: !!cfg.capi_enabled,
        capi_access_token: (cfg.capi_access_token || "").trim(),
        notes: cfg.notes || "",
      };
      const r = await axios.put(`${API}/admin/settings/meta_pixel`, payload, { headers: adminHeaders() });
      setCfg({ ...cfg, ...r.data });
      toast.success("Meta Pixel + CAPI settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveOtp = async () => {
    if (otpCfg?.enabled_live && (!otpCfg.auth_key.trim() || !otpCfg.template_id.trim())) {
      toast.error("MSG91 auth key and template/flow id are required before going live.");
      return;
    }
    setSavingOtp(true);
    try {
      const payload = {
        enabled: !!otpCfg.enabled,
        enabled_live: !!otpCfg.enabled_live,
        provider: otpCfg.provider || "mock",
        auth_key: (otpCfg.auth_key || "").trim(),
        template_id: (otpCfg.template_id || "").trim(),
        sender_id: (otpCfg.sender_id || "").trim(),
        var_name: (otpCfg.var_name || "otp").trim(),
        resend_cooldown: Number(otpCfg.resend_cooldown) || 30,
      };
      const r = await axios.put(`${API}/admin/settings/otp`, payload, { headers: adminHeaders() });
      setOtpCfg({ ...otpCfg, ...r.data });
      toast.success("OTP settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingOtp(false);
    }
  };

  const testFire = () => {
    setTesting(true);
    try {
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("trackCustom", "HerbKrantiAdminTest", { source: "admin", at: new Date().toISOString() });
        toast.success("Sent a test trackCustom event. Check Meta Events Manager.");
      } else {
        toast.message("fbq not loaded on admin route (expected). Test from a storefront page instead.");
      }
    } finally {
      setTimeout(() => setTesting(false), 400);
    }
  };

  if (!cfg || !otpCfg) return <AdminLayout title="Integrations"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;

  return (
    <AdminLayout title="Integrations" subtitle="Third-party services connected to HerbKranti">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Meta Pixel + CAPI */}
        <div className="hk-card p-5 space-y-4 md:col-span-2" data-testid="meta-pixel-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="hk-h5">Meta Pixel + Conversions API</h3>
              <p className="text-[12.5px] text-[var(--hk-ink-2)] mt-1 leading-relaxed">
                Client-side Pixel fires PageView / ViewContent / AddToCart / InitiateCheckout / Purchase.
                Enable CAPI + paste an access token to also fire Purchase <b>server-side</b> — recovers iOS 14+
                signals that browsers block, and deduplicates with the client event using the order id.
              </p>
            </div>
            <span className={"text-[11px] px-2 py-1 rounded-full border " + (cfg.enabled ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-[var(--hk-border)] text-[var(--hk-ink-2)]")}
              data-testid="meta-pixel-status-pill">
              {cfg.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="meta-pixel-enabled-toggle">
              <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} className="w-4 h-4" />
              <span className="text-[13.5px]">Fire Meta Pixel on the storefront</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="meta-capi-enabled-toggle">
              <input type="checkbox" checked={!!cfg.capi_enabled} onChange={(e) => set("capi_enabled", e.target.checked)} className="w-4 h-4" />
              <span className="text-[13.5px]">Fire server-side Conversions API (Purchase)</span>
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Pixel ID</span>
              <input
                value={cfg.pixel_id}
                onChange={(e) => set("pixel_id", e.target.value.replace(/[^\d]/g, ""))}
                placeholder="e.g. 999102929841792"
                data-testid="meta-pixel-id-input"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]"
              />
              <p className="text-[11px] text-[var(--hk-ink-2)] mt-1">Numeric ID from Meta Events Manager.</p>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Test Event Code (optional)</span>
              <input
                value={cfg.test_event_code}
                onChange={(e) => set("test_event_code", e.target.value)}
                placeholder="TEST12345"
                data-testid="meta-pixel-test-code-input"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]"
              />
              <p className="text-[11px] text-[var(--hk-ink-2)] mt-1">Used by Meta Events Manager → Test Events tab for both Pixel + CAPI.</p>
            </label>
            <label className="block md:col-span-2">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">CAPI Access Token</span>
              <input
                value={cfg.capi_access_token}
                onChange={(e) => set("capi_access_token", e.target.value)}
                placeholder="EAA... (paste your System User token)"
                data-testid="meta-capi-token-input"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] font-mono"
              />
              <p className="text-[11px] text-[var(--hk-ink-2)] mt-1">
                Get it: Meta Business Settings → System Users → Add → Generate New Token with{" "}
                <code className="text-[10.5px]">ads_management</code> + <code className="text-[10.5px]">business_management</code>.
                Stored server-side; masked after save.
              </p>
            </label>
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-2">
            <button onClick={save} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid="save-meta-pixel">
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={testFire} disabled={testing} className="hk-btn-ghost h-10 px-4" data-testid="test-meta-pixel">
              Send Test Pixel Event
            </button>
            <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer" className="text-[12px] text-[var(--hk-ink-2)] underline">
              Open Meta Events Manager →
            </a>
          </div>
        </div>

        {/* OTP */}
        <div className="hk-card p-5 space-y-4 md:col-span-2" data-testid="otp-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="hk-h5">Checkout Mobile OTP (MSG91)</h3>
              <p className="text-[12.5px] text-[var(--hk-ink-2)] mt-1 leading-relaxed">
                Verifies the customer's phone number via SMS OTP before Place Order unlocks.
                <b> Checkout only shows this step when Enabled + Go Live + valid provider credentials are all set.</b> Otherwise the checkout goes straight to Proceed to Payment.
              </p>
            </div>
            <span className={"text-[11px] px-2 py-1 rounded-full border " + (
              (otpCfg.enabled && otpCfg.enabled_live && (otpCfg.provider === "mock" || (otpCfg.provider === "msg91" && otpCfg.auth_key && otpCfg.template_id)))
                ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                : (otpCfg.enabled ? "border-amber-300 text-amber-700 bg-amber-50" : "border-[var(--hk-border)] text-[var(--hk-ink-2)]")
            )}
              data-testid="otp-status-pill">
              {(() => {
                const valid = otpCfg.provider === "mock" || (otpCfg.provider === "msg91" && otpCfg.auth_key && otpCfg.template_id);
                if (!otpCfg.enabled) return "Disabled — hidden on checkout";
                if (!otpCfg.enabled_live) return "Ready — flip Go Live to show on checkout";
                if (!valid) return "Missing credentials — hidden on checkout";
                return otpCfg.provider === "msg91" ? "Live (MSG91) — visible on checkout" : "Live (Mock) — visible on checkout";
              })()}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="otp-enabled-toggle">
              <input type="checkbox" checked={!!otpCfg.enabled} onChange={(e) => setOtp("enabled", e.target.checked)} className="w-4 h-4" />
              <span className="text-[13.5px]">Require OTP verification at checkout</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="otp-live-toggle">
              <input type="checkbox" checked={!!otpCfg.enabled_live} onChange={(e) => setOtp("enabled_live", e.target.checked)} className="w-4 h-4" />
              <span className="text-[13.5px]">Go live (use MSG91 SMS instead of mock)</span>
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">MSG91 Auth Key</span>
              <input
                value={otpCfg.auth_key}
                onChange={(e) => setOtp("auth_key", e.target.value)}
                placeholder="Paste your MSG91 authkey"
                data-testid="otp-authkey-input"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] font-mono"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Template / Flow ID</span>
              <input
                value={otpCfg.template_id}
                onChange={(e) => setOtp("template_id", e.target.value)}
                placeholder="e.g. 66abc123..."
                data-testid="otp-template-input"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Sender ID</span>
              <input
                value={otpCfg.sender_id}
                onChange={(e) => setOtp("sender_id", e.target.value)}
                placeholder="HKRANT"
                data-testid="otp-sender-input"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Variable Name (in template)</span>
              <input
                value={otpCfg.var_name}
                onChange={(e) => setOtp("var_name", e.target.value)}
                placeholder="otp"
                data-testid="otp-varname-input"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Resend Cooldown (seconds)</span>
              <input
                type="number"
                min={10}
                max={300}
                value={otpCfg.resend_cooldown}
                onChange={(e) => setOtp("resend_cooldown", Number(e.target.value))}
                data-testid="otp-cooldown-input"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]"
              />
            </label>
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-2">
            <button onClick={saveOtp} disabled={savingOtp} className="hk-btn-primary h-10 px-4" data-testid="save-otp">
              {savingOtp ? "Saving…" : "Save OTP settings"}
            </button>
            <a href="https://control.msg91.com/app/api" target="_blank" rel="noreferrer" className="text-[12px] text-[var(--hk-ink-2)] underline">
              Get MSG91 API keys →
            </a>
          </div>
        </div>

        {/* Telegram Notifications */}
        {tgCfg && (
        <div className="hk-card p-5 space-y-4 md:col-span-2" data-testid="telegram-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="hk-h5">Telegram Notifications</h3>
              <p className="text-[12.5px] text-[var(--hk-ink-2)] mt-1 leading-relaxed">
                Sends selected e-commerce + visitor events to your private Telegram chat/group.
                Bot Token is stored server-side and never exposed to the browser or public API.
                Failures never block checkout or the website.
              </p>
            </div>
            <span className={"text-[11px] px-2 py-1 rounded-full border " + (tgCfg.enabled && tgCfg.bot_token && tgCfg.chat_id ? "border-emerald-300 text-emerald-700 bg-emerald-50" : tgCfg.enabled ? "border-amber-300 text-amber-700 bg-amber-50" : "border-[var(--hk-border)] text-[var(--hk-ink-2)]")} data-testid="telegram-status-pill">
              {!tgCfg.enabled ? "Disabled" : (!tgCfg.bot_token || !tgCfg.chat_id ? "Missing credentials" : "Live")}
            </span>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer" data-testid="telegram-enabled-toggle">
            <input type="checkbox" checked={!!tgCfg.enabled} onChange={(e) => setTg("enabled", e.target.checked)} className="w-4 h-4" />
            <span className="text-[13.5px]">Enable Telegram notifications globally</span>
          </label>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Bot Token</span>
              <input value={tgCfg.bot_token} onChange={(e) => setTg("bot_token", e.target.value)} placeholder="123456:AA…" data-testid="telegram-token-input" className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] font-mono" />
              <p className="text-[11px] text-[var(--hk-ink-3)] mt-1">Create a bot via <b>@BotFather</b> → /newbot. Stored server-side; masked after save.</p>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Chat ID</span>
              <input value={tgCfg.chat_id} onChange={(e) => setTg("chat_id", e.target.value)} placeholder="123456789 or -100…" data-testid="telegram-chatid-input" className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] font-mono" />
              <p className="text-[11px] text-[var(--hk-ink-3)] mt-1">DM your bot then visit <code>getUpdates</code>, or add bot to a channel/group and grab its chat id.</p>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">High Value Order Threshold (₹)</span>
              <input type="number" min={0} value={tgCfg.high_value_threshold_rupees} onChange={(e) => setTg("high_value_threshold_rupees", Number(e.target.value))} data-testid="telegram-threshold-input" className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]" />
              <p className="text-[11px] text-[var(--hk-ink-3)] mt-1">Fires an extra 🔥 High Value Order alert when a paid order ≥ this amount.</p>
            </label>
            <div className="text-[11px] text-[var(--hk-ink-3)] self-end">
              {tgCfg.last_success_at && <div>Last success: <b>{new Date(tgCfg.last_success_at).toLocaleString("en-IN")}</b></div>}
              {tgCfg.last_error && <div className="text-red-700">Last error: {tgCfg.last_error}</div>}
            </div>
          </div>

          {/* Event toggles */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)] mb-2">Events to send</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-y-1.5 gap-x-4">
              {[
                ["new_visitor", "New Visitor"],
                ["returning_visitor", "Returning Visitor"],
                ["pageview", "Every PageView"],
                ["product_viewed", "Product Viewed"],
                ["add_to_cart", "Add to Cart"],
                ["checkout_started", "Checkout Started"],
                ["payment_successful", "Payment Successful"],
                ["payment_failed", "Payment Failed"],
                ["payment_cancelled", "Payment Cancelled"],
                ["order_confirmed", "New Confirmed Order"],
                ["high_value_order", "High Value Order"],
                ["order_shipped", "Order Shipped"],
                ["order_delivered", "Order Delivered"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`telegram-event-${k}`}>
                  <input type="checkbox" checked={!!tgCfg.events?.[k]} onChange={(e) => setTgEvent(k, e.target.checked)} className="w-4 h-4" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-2">
            <button onClick={saveTg} disabled={savingTg} className="hk-btn-primary h-10 px-4" data-testid="save-telegram">
              {savingTg ? "Saving…" : "Save Telegram Settings"}
            </button>
            <button onClick={testTgConnection} className="hk-btn-ghost h-10 px-4" data-testid="test-telegram-connection">Test Connection</button>
            <button onClick={sendTgTestMsg} className="hk-btn-ghost h-10 px-4" data-testid="send-telegram-test">Send Test Message</button>
            <button onClick={loadTgLog} className="hk-btn-ghost h-10 px-4" data-testid="load-telegram-log">Load recent log</button>
          </div>

          {tgLog.length > 0 && (
            <div className="mt-3 border border-[var(--hk-border)] rounded-md overflow-hidden" data-testid="telegram-log-table">
              <table className="w-full text-[12px]">
                <thead className="bg-[var(--hk-warm-ivory)] text-[10px] uppercase tracking-widest text-[var(--hk-ink-2)]">
                  <tr><th className="text-left px-3 py-2">Event</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Info</th></tr>
                </thead>
                <tbody>
                  {tgLog.map((l) => (
                    <tr key={l.id} className="border-t border-[var(--hk-border)]">
                      <td className="px-3 py-1.5 font-mono">{l.event}</td>
                      <td className="px-3 py-1.5">
                        <span className={"text-[10px] px-1.5 py-0.5 rounded border " + (l.status === "sent" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : l.status === "failed" ? "border-red-300 bg-red-50 text-red-700" : "border-stone-300 bg-stone-50 text-stone-600")}>{l.status}</span>
                      </td>
                      <td className="px-3 py-1.5 text-[var(--hk-ink-3)]">{new Date(l.created_at).toLocaleTimeString("en-IN")}</td>
                      <td className="px-3 py-1.5 truncate max-w-[280px] text-[var(--hk-ink-3)]">{JSON.stringify(l.meta || {})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* Google Maps — delivery location */}
        {gmCfg && (
        <div className="hk-card p-5 space-y-4 md:col-span-2" data-testid="google-maps-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="hk-h5">Google Maps — Delivery Location</h3>
              <p className="text-[12.5px] text-[var(--hk-ink-2)] mt-1 leading-relaxed">
                Lets customers pin their exact doorstep at checkout (current location, address search,
                map pin, or a pasted Maps link). Saved privately on the order — visible only to Admin,
                never sent to Meta Pixel, Telegram, or ad platforms. The API key is client-side by
                design — restrict it by HTTP referrer in Google Cloud Console.
              </p>
            </div>
            <span className={"text-[11px] px-2 py-1 rounded-full border " + (gmCfg.enabled && gmCfg.api_key ? "border-emerald-300 text-emerald-700 bg-emerald-50" : gmCfg.enabled ? "border-amber-300 text-amber-700 bg-amber-50" : "border-[var(--hk-border)] text-[var(--hk-ink-2)]")} data-testid="google-maps-status-pill">
              {!gmCfg.enabled ? "Disabled" : (!gmCfg.api_key ? "Missing API key" : "Live")}
            </span>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer" data-testid="google-maps-enabled-toggle">
            <input type="checkbox" checked={!!gmCfg.enabled} onChange={(e) => setGm("enabled", e.target.checked)} className="w-4 h-4" />
            <span className="text-[13.5px]">Enable Google Maps at checkout</span>
          </label>

          <label className="block max-w-xl">
            <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Google Maps API Key</span>
            <input value={gmCfg.api_key} onChange={(e) => setGm("api_key", e.target.value)} placeholder="AIza…" data-testid="google-maps-key-input" className="mt-1.5 w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] font-mono" />
            <p className="text-[11px] text-[var(--hk-ink-3)] mt-1">
              Google Cloud Console → enable <b>Maps JavaScript API</b>, <b>Places API</b> &amp; <b>Geocoding API</b> → create key → restrict by HTTP referrer (<code>https://yourdomain.com/*</code>).
            </p>
          </label>

          <div>
            <div className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)] mb-2">Checkout features</div>
            <div className="grid sm:grid-cols-2 gap-y-1.5 gap-x-4">
              {[
                ["autocomplete", "Address Autocomplete (Places search)"],
                ["current_location", "Use My Current Location"],
                ["map_pin", "Drop / Select Pin on Map"],
                ["paste_link", "Paste Google Maps Link"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`google-maps-feature-${k}`}>
                  <input type="checkbox" checked={!!gmCfg[k]} onChange={(e) => setGm(k, e.target.checked)} className="w-4 h-4" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-2">
            <button onClick={saveGm} disabled={savingGm} className="hk-btn-primary h-10 px-4" data-testid="save-google-maps">
              {savingGm ? "Saving…" : "Save Google Maps Settings"}
            </button>
            <a href="https://console.cloud.google.com/google/maps-apis/start" target="_blank" rel="noreferrer" className="text-[12px] text-[var(--hk-ink-2)] underline">
              Get a Google Maps API key →
            </a>
          </div>
        </div>
        )}

        {/* Informational cards */}
        <div className="hk-card p-5"><h3 className="hk-h5">Razorpay</h3><p className="text-[13px] text-[var(--hk-ink-2)] mt-2">Configured under <b>Payment Gateways</b>.</p></div>
        <div className="hk-card p-5"><h3 className="hk-h5">Shiprocket / Delhivery</h3><p className="text-[13px] text-[var(--hk-ink-2)] mt-2">Live-API wiring is active. Paste your Shiprocket email + password under <b>Logistics</b>, mark it Active, and Save. New paid orders can then be dispatched via Shiprocket with real AWB + label PDFs.</p></div>
        <div className="hk-card p-5" data-testid="email-card">
          <h3 className="hk-h5">Email (Transactional)</h3>
          <p className="text-[13px] text-[var(--hk-ink-2)] mt-2">Powered by Emergent-managed Resend. Order-paid, dispatched, and delivered emails auto-fire to the customer + <b>Global Settings → Support email</b>.</p>
          <button
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/admin/email/test`, {}, { headers: adminHeaders() });
                toast.success(`Test email queued (${r.data?.email_id || "ok"}) to ${r.data?.to}`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Email test failed");
              }
            }}
            className="hk-btn-ghost h-9 px-3 mt-3"
            data-testid="email-test-btn"
          >
            Send Test Email
          </button>
        </div>
        <div className="hk-card p-5"><h3 className="hk-h5">WhatsApp</h3><p className="text-[13px] text-[var(--hk-ink-2)] mt-2">Configured under <b>Global Settings → WhatsApp number</b>.</p></div>
      </div>
    </AdminLayout>
  );
};
